import { globalStores } from '@/editor/stores';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { Color } from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import BaseModel2d from './BaseModel2d';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { LineSegments2, LineSegmentsGeometry } from 'three/examples/jsm/Addons.js';
import { getRotatedRectangleVertices } from '@/editor/math/utils';
import { isNumber } from 'lodash-es';
import { IBoxesEntityParams } from '@/editor/types/entity';

export class BoxesOutlineModel extends BaseModel2d {
  private material: LineMaterial = new LineMaterial({
    color: new Color(0x000000),
    depthTest: false,
    transparent: true,
    linewidth: 1
  });
  constructor() {
    super();

    this.setDisplayOrder(ENTITY_RENDER_ORDER.selectedOutline);
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
  }

  createModel(params: IBoxesEntityParams) {
    const vertices: number[] = [];
    for (const box of params.boxes) {
      const [_lb, _rb, _rt, _lt] = getRotatedRectangleVertices(
        box.width,
        box.thickness,
        { x: box.x, y: box.y },
        box.rotation
      );

      const lb = [_lb.x / pixelRatio2d, _lb.y / pixelRatio2d, 0];
      const rb = [_rb.x / pixelRatio2d, _rb.y / pixelRatio2d, 0];
      const rt = [_rt.x / pixelRatio2d, _rt.y / pixelRatio2d, 0];
      const lt = [_lt.x / pixelRatio2d, _lt.y / pixelRatio2d, 0];

      vertices.push(...lb, ...rb, ...rb, ...rt, ...rt, ...lt, ...lt, ...lb);
    }

    const color = new Color(params.color);

    const outlineGeometry = new LineSegmentsGeometry();
    outlineGeometry.setPositions(vertices);
    this.material.color = color;
    this.material.linewidth = params.linewidth;
    const outline = new LineSegments2(outlineGeometry, this.material);

    outline.renderOrder = params.renderOrder ?? ENTITY_RENDER_ORDER.selectedOutline;

    this._modelGroup.position.set(params.x / pixelRatio2d, params.y / pixelRatio2d, params.z / pixelRatio2d);
    this._modelGroup.rotation.z = params.rotation;

    this._modelGroup.add(outline);
  }

  update(params: Pick<IBoxesEntityParams, 'x' | 'y' | 'z' | 'rotation'>) {
    if (isNumber(params.x)) {
      this._modelGroup.position.x = params.x / pixelRatio2d;
    }
    if (isNumber(params.y)) {
      this._modelGroup.position.y = params.y / pixelRatio2d;
    }
    if (isNumber(params.z)) {
      this._modelGroup.position.z = params.z / pixelRatio2d;
    }
    if (isNumber(params.rotation)) {
      this._modelGroup.rotation.z = params.rotation;
    }
  }
}
